/*
 * Yuri ATC loading shim + surface tracking.
 *
 * Problem: GoKwik's per-button click handler (from side-cart.js) preventDefaults
 * the form submit for ATC buttons that live INSIDE form[action='/cart/add'].
 * That prevents the theme's <product-form> from setting aria-busy, so the
 * card ATC and PDP inline ATC show no loading indication on tap. Only the
 * sticky ATC (which references the form via HTML5 form="id" attr and is
 * therefore outside any form in the DOM tree) escapes the interception and
 * shows the dots animation.
 *
 * Fix: attach a capture-phase click listener on document. Capture-phase
 * listeners on ancestors always fire before capture-phase listeners on the
 * target node, so we run before GoKwik regardless of load order or their
 * stopImmediatePropagation call. We do two things per click:
 *   1) push a dataLayer 'atc_click_surface' event with the surface derived
 *      from the button's DOM context (card / pdp_inline / pdp_sticky).
 *   2) for buttons inside form[action='/cart/add'] (the GoKwik-intercepted
 *      set), set aria-busy="true" ourselves so the theme's <button-content>
 *      dots animate. Sticky buttons are left alone — the theme already
 *      handles aria-busy for those via its own submit path.
 *
 * Cleanup: aria-busy is removed on (a) any GoKwik/side-cart drawer becoming
 * visible under body, (b) pagehide, (c) a hard 8s failsafe timeout.
 */
(function () {
  'use strict';

  var PENDING = new Set();

  function surfaceOf(btn) {
    if (btn.closest('product-sticky-add-to-cart')) return 'pdp_sticky';
    if (btn.closest('product-card')) return 'card';
    if (btn.closest('buy-buttons')) return 'pdp_inline';
    return null;
  }

  function pageTypeOf(path) {
    if (path === '/' || path === '') return 'home';
    if (path.indexOf('/products/') === 0) return 'pdp';
    if (path.indexOf('/collections/') === 0) return 'collection';
    if (path.indexOf('/search') === 0) return 'search';
    return 'other';
  }

  function clearBusy(btn) {
    if (!btn) return;
    btn.removeAttribute('aria-busy');
    PENDING.delete(btn);
  }

  function clearAllBusy() {
    PENDING.forEach(clearBusy);
  }

  // 1500 ms is a comfortable window for the dots to render (GoKwik cart-add
  // typically resolves in 300–800 ms) while ensuring the button never stays
  // stuck if the drawer MutationObserver misses. Users closing the drawer
  // and coming back should never see stale dots.
  function scheduleFailsafe(btn) {
    setTimeout(function () { clearBusy(btn); }, 1500);
  }

  // Detect GoKwik / Shopify cart drawer opening via body mutations.
  // We accept a broad set of selectors so we're robust to GoKwik naming changes.
  var DRAWER_SELECTORS = [
    '#gk-side-cart',
    '.gk-side-cart',
    '.kwik-cart-container',
    '[data-kwik-cart]',
    'gk-side-cart',
    'cart-drawer[open]',
    '.cart-drawer.is-open'
  ].join(',');

  function looksLikeOpenDrawer(node) {
    if (!(node instanceof Element)) return false;
    if (node.matches && node.matches(DRAWER_SELECTORS)) return true;
    if (node.querySelector && node.querySelector(DRAWER_SELECTORS)) return true;
    return false;
  }

  var drawerObserver = new MutationObserver(function (mutations) {
    for (var i = 0; i < mutations.length; i++) {
      var m = mutations[i];
      // New drawer element inserted
      if (m.addedNodes && m.addedNodes.length) {
        for (var j = 0; j < m.addedNodes.length; j++) {
          if (looksLikeOpenDrawer(m.addedNodes[j])) { clearAllBusy(); return; }
        }
      }
      // Existing drawer element flipped visibility / class
      if (m.type === 'attributes' && m.target instanceof Element && m.target.matches(DRAWER_SELECTORS)) {
        var style = window.getComputedStyle(m.target);
        if (style && style.display !== 'none' && style.visibility !== 'hidden') { clearAllBusy(); return; }
      }
    }
  });

  function startObserving() {
    if (!document.body) return false;
    drawerObserver.observe(document.body, {
      childList: true, subtree: true,
      attributes: true, attributeFilter: ['class', 'style', 'open', 'hidden']
    });
    return true;
  }
  if (!startObserving()) {
    document.addEventListener('DOMContentLoaded', startObserving, { once: true });
  }

  window.addEventListener('pagehide', clearAllBusy);

  // Main capture-phase click handler.
  document.addEventListener('click', function (event) {
    var target = event.target;
    if (!target || !target.closest) return;
    var btn = target.closest('button[type="submit"]');
    if (!btn) return;
    var surface = surfaceOf(btn);
    if (!surface) return;

    // 1) Tracking: fire ONCE per user tap, independent of whether GoKwik or
    //    the theme handles the actual cart-add. Keeps the standard
    //    `add_to_cart` event untouched to avoid double-count.
    //
    // Yuri's storefront does NOT load gtag.js — Shopify's own Web Pixels
    // (webPixelsManager + Monorail) forwards a fixed schema of events
    // (add_to_cart, begin_checkout, view_item, etc.) server-side. Custom
    // events fired via gtag()/dataLayer.push do NOT reach GA4 in this
    // configuration. So we send the event directly to GA4's Measurement
    // Protocol endpoint. Reuses the visitor's own _ga cookie for client_id
    // and _ga_<mid> for session_id, so the event ties into the same
    // session/user as the theme's native events.
    var GA4_MEASUREMENT_ID = 'G-VH3BZTYY3Q';  // from window.gaTag.ga4 on live pages
    var params = {
      button_surface: surface,
      button_page_type: pageTypeOf(window.location.pathname)
    };
    // Also fire via gtag/dataLayer as a defense-in-depth (harmless no-ops on
    // this store today, but if gtag.js is added later they light up
    // automatically).
    try {
      if (typeof window.gtag === 'function') {
        window.gtag('event', 'atc_click_surface', params);
      }
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(Object.assign({ event: 'atc_click_surface' }, params));
    } catch (e) {}
    // The real delivery channel: direct Measurement Protocol.
    try {
      var cid = '';
      var sid = '';
      var m1 = document.cookie.match(/(?:^|;\s*)_ga=GA1\.\d+\.([^;]+)/);
      if (m1) cid = m1[1];
      var m2 = document.cookie.match(new RegExp('(?:^|;\\s*)_ga_' + GA4_MEASUREMENT_ID.replace(/^G-/, '') + '=GS1\\.\\d+\\.(\\d+)'));
      if (m2) sid = m2[1];
      if (!cid) {
        // Ephemeral fallback so the event still ships, though it won't
        // tie into the session (rare — happens only if user has no _ga
        // cookie yet, e.g. cookie-blocked).
        cid = (Math.floor(Math.random() * 1e10) + '.' + Math.floor(Date.now() / 1000));
      }
      var qs = 'v=2'
        + '&tid=' + encodeURIComponent(GA4_MEASUREMENT_ID)
        + '&cid=' + encodeURIComponent(cid)
        + '&_s=1'
        + '&dl=' + encodeURIComponent(window.location.href)
        + '&dt=' + encodeURIComponent(document.title || '')
        + '&en=atc_click_surface'
        + '&ep.button_surface=' + encodeURIComponent(surface)
        + '&ep.button_page_type=' + encodeURIComponent(params.button_page_type);
      if (sid) qs += '&sid=' + encodeURIComponent(sid);
      var url = 'https://www.google-analytics.com/g/collect?' + qs;
      // sendBeacon is preferred — survives the page navigation that follows
      // a successful cart-add. Fallback to fetch keepalive.
      if (navigator.sendBeacon) {
        navigator.sendBeacon(url, '');
      } else if (window.fetch) {
        window.fetch(url, { method: 'POST', keepalive: true, mode: 'no-cors' });
      }
    } catch (e) { /* MP delivery failure is non-fatal */ }

    // 2) Loading indication: only for buttons where the theme won't set
    //    aria-busy itself (i.e., buttons inside form[action='/cart/add'],
    //    which is exactly the set GoKwik intercepts). Sticky buttons live
    //    outside any form (HTML5 form="id" reference), so the theme's
    //    <product-form> handles their aria-busy already — don't collide.
    if (btn.closest('form[action*="/cart/add"]')) {
      btn.setAttribute('aria-busy', 'true');
      PENDING.add(btn);
      scheduleFailsafe(btn);
    }
  }, true /* capture: fires before GoKwik's per-button handler */);

  // Prevent aria-busy leakage to non-submitter buttons of the same form.
  //
  // The theme's <product-form> submit handler (theme.js ~L3112) iterates
  //   Array.from(form.elements).filter(b => b.type === 'submit')
  // and sets aria-busy on every match. form.elements is a live collection
  // that includes buttons OUTSIDE the form referencing it via HTML5
  // form="id", so tapping the sticky ATC makes the below-price button and
  // the other sticky variant animate dots too.
  //
  // v2 removed aria-busy on those buttons in bubble phase. That worked for
  // the dots animation but produced a ~10ms label flash: <button-content>'s
  // MutationObserver still saw the set/unset pair, and the effective
  // transition (aria-busy: null → null) causes onLoadingChanged(false) to
  // run — which animates the label opacity 0→1 even though the button was
  // never in a loading state. Visible flicker.
  //
  // v3 prevents the set in the first place. In capture phase (before the
  // theme's target-phase handler runs), temporarily flip non-submitter
  // buttons' .type from 'submit' to 'button'. Theme's filter then excludes
  // them entirely. Restore in bubble phase, after the theme has already
  // snapshotted its submitButtons list. Nothing to remove → no observer
  // fires → no flicker.
  //
  // Safe because .type is only read by theme in that one filter and the
  // mutation window is a single synchronous event dispatch. GoKwik-
  // intercepted clicks never fire a submit event, so this is a no-op for
  // card / below-price taps.
  var TYPE_RESTORE = [];

  document.addEventListener('submit', function (event) {
    if (!event.submitter) return;
    var form = event.target;
    if (!form || form.tagName !== 'FORM') return;
    if (!form.action || form.action.indexOf('/cart/add') === -1) return;
    var submitter = event.submitter;
    var els = form.elements;
    for (var i = 0; i < els.length; i++) {
      var el = els[i];
      if (el.type === 'submit' && el !== submitter) {
        el.type = 'button';
        TYPE_RESTORE.push(el);
      }
    }
  }, true /* capture: runs before theme's target-phase handler */);

  document.addEventListener('submit', function () {
    while (TYPE_RESTORE.length) {
      TYPE_RESTORE.pop().type = 'submit';
    }
  }, false /* bubble: runs after theme has snapshotted submitButtons */);
})();
